import { ReactNode } from 'react';
import styled from 'styled-components';

import { ActionCell } from '#/components/rfq-common/ActionButtons';
import { useRowIndex } from '#/components/UnifiedRfqs/components/RfqRowBase';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { RfqActionRow } from '#/components/UnifiedRfqs/rfqs/action/common';
import {
  blackTangaroa,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

export interface Props {
  readonly className?: string;
  readonly bidQty?: ReactNode;
  readonly bidPrice?: ReactNode;
  readonly mark?: ReactNode;
  readonly askPrice?: ReactNode;
  readonly askQty?: ReactNode;
  readonly bidKill?: ReactNode;
  readonly askKill?: ReactNode;
  readonly offsetAsk?: ReactNode;
  readonly offsetBid?: ReactNode;
  readonly bidFlashKey?: string;
  readonly askFlashKey?: string;
  readonly markIv?: ReactNode;
  readonly askIv?: ReactNode;
  readonly bidIv?: ReactNode;
  readonly isBetterThanScreenBestBid?: boolean;
  readonly isBetterThanScreenBestAsk?: boolean;
}

export function BaseRfqAction(props: Props) {
  const {
    className,
    askQty,
    bidQty,
    bidPrice,
    askPrice,
    askKill,
    bidKill,
    mark,
    markIv,
    bidIv,
    askIv,
    offsetAsk,
    offsetBid,
    bidFlashKey,
    askFlashKey,
    isBetterThanScreenBestAsk = false,
    isBetterThanScreenBestBid = false,
  } = props;

  const rowIndex = useRowIndex();
  const isMobile = useIsMobile();
  return (
    <Row className={className} aria-label="bid ask">
      {!isMobile && bidFlashKey !== undefined && (
        <BidAnimation
          key={bidFlashKey}
          data-testid={`rfq_bid_flash_${rowIndex}`}
        />
      )}
      <QuantityCell
        area="bqty"
        role="cell"
        data-testid={`rfq_bid_qty_${rowIndex}`}
      >
        {bidQty}
      </QuantityCell>
      <ActionCell
        area="bkill"
        role="cell"
        data-testid={`rfq_bid_cancel_${rowIndex}`}
      >
        {bidKill}
      </ActionCell>
      <OffsetCell
        area="bid"
        role="cell"
        data-testid={`rfq_bid_price_${rowIndex}`}
      >
        {bidPrice}
        {offsetBid != null && (
          <OffsetValue
            data-testid={`rfq_bid_price_offset_${rowIndex}`}
            isBetterThanScreen={isBetterThanScreenBestBid}
          >
            {offsetBid}
          </OffsetValue>
        )}
        {bidIv != null && bidIv !== '' && (
          <OffsetValue data-testid={`rfq_bid_iv_value${rowIndex}`}>
            {bidIv}
          </OffsetValue>
        )}
      </OffsetCell>
      <MarketCell area="mark" role="cell" data-testid="rfq_mark_price">
        <MarkCell
          css={`
            margin-top: ${markIv != null ? '8px' : '0'};
            padding: 0 0.25rem;
          `}
        >
          {mark}
        </MarkCell>
        {markIv != null && markIv !== '' && (
          <IvValue data-testid={`rfq_iv_value_${rowIndex}`}>{markIv}</IvValue>
        )}
      </MarketCell>
      <OffsetCell
        area="ask"
        role="cell"
        data-testid={`rfq_ask_price_${rowIndex}`}
      >
        {askPrice}
        {offsetAsk != null && (
          <OffsetValue
            data-testid={`rfq_ask_price_offset_${rowIndex}`}
            isBetterThanScreen={isBetterThanScreenBestAsk}
          >
            {offsetAsk}
          </OffsetValue>
        )}
        {askIv != null && askIv !== '' && (
          <OffsetValue data-testid={`rfq_ask_iv_value${rowIndex}`}>
            {askIv}
          </OffsetValue>
        )}
      </OffsetCell>
      <ActionCell
        area="akill"
        role="cell"
        data-testid={`rfq_ask_cancel_${rowIndex}`}
      >
        {askKill}
      </ActionCell>
      {!isMobile && askFlashKey !== undefined && (
        <AskAnimation
          key={askFlashKey}
          data-testid={`rfq_ask_flash_${rowIndex}`}
        />
      )}
      <QuantityCell
        area="aqty"
        role="cell"
        data-testid={`rfq_ask_qty_${rowIndex}`}
      >
        {askQty}
      </QuantityCell>
    </Row>
  );
}

const Row = styled(RfqActionRow)`
  grid-area: quotes;
  background: ${blackTangaroa};
`;

const QuantityCell = styled(ActionCell)`
  color: #b3b6ba;
  font-size: 13px;
  font-weight: 600;
  z-index: 2;
`;

const MarketCell = styled(ActionCell)`
  display: flex;
  flex-direction: column;
  color: #81868b;
  font-size: 13px;
  font-weight: 600;
`;

const MarkCell = styled.div`
  font-weight: 600;
`;

const OffsetCell = styled(ActionCell)`
  display: flex;
  flex-direction: column;
  z-index: 2;
  font-size: 14px;
`;

const OffsetValue = styled.div<{
  isBetterThanScreen?: boolean;
}>`
  opacity: ${({ isBetterThanScreen }) =>
    isBetterThanScreen === true ? 1 : 0.4};
  color: ${({ isBetterThanScreen }) =>
    isBetterThanScreen === true ? `${paradigmLightBlue}` : 'white'};

  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0;
  line-height: 12px;
  text-align: center;
  margin-top: 1px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 12px;
  }
`;

const IvValue = styled(OffsetValue)`
  opacity: 0.3;
`;

const BidAnimation = styled.span`
  height: 46.5px;
  position: absolute;
  width: 180px;
  animation: flash 1s;
  animation-fill-mode: 'forwards';
  background-color: #3b77d4;
  opacity: 0;
  z-index: 1;
`;

const AskAnimation = styled(BidAnimation)`
  right: 0px;
`;
